import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AviationEvent, AviationEventType, AviationEventTypeDesc, TimeZones } from '../../shared/models/aviation-event.model';
import { MAT_DIALOG_DATA, MatDatepickerInputEvent, MatDialogRef, MatSelectChange, MatSnackBar } from '@angular/material';
import { AviationEventsService } from '../../shared/services/aviation-events.service';
import { AirportSearchResult } from '../../airport/models/airport-search-result.model';
import { AirportService } from '../../airport/airport.service';
import { LatLon } from '../../util/geodesy/latlon';
import { GoogleService } from '../../google/google.service';
import { GoogleMapsValidationModel } from '../../google/google-maps-validation.model';
import { AddressValidationService } from '../../google/address-validation.service';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { eventUSStates, eventCanadianProvinces, eventCountries, eventAMPM, eventHours, eventMinutes } from '../../globals';
import Granularity = GoogleMapsValidationModel.Granularity;
import { Country } from '../../shared/models/country.model';
import { Timezone } from '../../shared/models/timezone.model';
import * as MomentTZ from 'moment-timezone';


const USTimezones: Timezone[] = [
  { id: 1, countryCode: 'America/Halifax', zoneName: 'ATLANTIC' },
  { id: 2, countryCode: 'America/New_York', zoneName: 'EASTERN' },
  { id: 3, countryCode: 'America/Chicago', zoneName: 'CENTRAL' },
  { id: 4, countryCode: 'America/Denver', zoneName: 'MOUNTAIN' },
  { id: 5, countryCode: 'America/Los_Angeles', zoneName: 'PACIFIC' },
  { id: 6, countryCode: 'Pacific/Honolulu', zoneName: 'HAWAII' },
  { id: 7, countryCode: 'America/Anchorage', zoneName: 'ALASKA' }
];

@Component({
  selector: 'app-edit-event-location',
  templateUrl: './edit-event-location.component.html',
  styleUrls: ['./edit-event-location.component.scss']
})
export class EditEventLocationComponent implements OnInit {
  formGroup: FormGroup;
  aviationEvent: AviationEvent;
  isLoading = true;
  eventCountries = eventCountries;
  airports: AirportSearchResult[] = [];
  selectedAirport: AirportSearchResult = null;
  airportSelectionError = false;
  showAddressFields = true;
  isFlightDeckAdmin = false;
  initialAirport = '';
  initialAirportId = 0;
  countries: Country[];
  timezones: Timezone[];
  currentCountry: string;
  initialCountryCode: string;
  eventTypes: String[];
  startDateMin: Date = new Date();
  endDateMin: Date = this.startDateMin;
  timeZones = Object.keys(TimeZones).sort();
  // tslint:disable-next-line:max-line-length
  urlRegex = /^((https?|http):\/\/)?(([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}|localhost)(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(\#[-a-z\d_]*)?$/i;
  eventStart: MomentTZ;
  eventEnd: MomentTZ;
  eventStartDateString: string;
  eventStartTimeString: string;
  eventEndDateString: string;
  eventEndTimeString: string;
  startDate: Date;
  endDate: Date;
  eventHours = eventHours;
  eventMinutes = eventMinutes;
  eventAMPM = eventAMPM;
  startHour: string;
  startMin: string;
  startAMPM: string;
  endHour: string;
  endMin: string;
  endAMPM: string;

  constructor(private _matDialogRef: MatDialogRef<EditEventLocationComponent>,
              @Inject(MAT_DIALOG_DATA) data,
              private _aviationEventsService: AviationEventsService,
              private _fb: FormBuilder,
              private _snackBar: MatSnackBar,
              private _airportService: AirportService,
              private _googleService: GoogleService,
              private _addressValidationService: AddressValidationService,
              private _businessManagerService: BusinessManagerService) {
    this.aviationEvent = data.aviationEvent;
    this.currentCountry = this.aviationEvent.country;
    this.initialCountryCode = this.aviationEvent.countryCode;
  }

  async ngOnInit() {
    this._matDialogRef.backdropClick().subscribe(() => {
      this._matDialogRef.close(null);
    });

    this.countries = await this._aviationEventsService.getCountries();
    this._businessManagerService.isFlightDeckAdmin().then((isAdmin) => this.isFlightDeckAdmin = isAdmin);

    if (this.aviationEvent.airportIdent && this.aviationEvent.airportIdent.length > 0) {
      this.initialAirportId = this.aviationEvent.airportId;
      await this._airportService.getAirport(this.aviationEvent.airportIdent, false).then((res) => {
        this.initialAirport = res.identifier + ': ' + res.name;
      }).catch(err => console.error(err));
    }

    /* let currentTimeZoneSimpleName = 'America/New_York';
    if (this.initialCountryCode === 'US') {
      console.log('this.aviationEvent.timezone: ', this.aviationEvent.timezone);
      currentTimeZoneSimpleName = USTimezones.find(i => i.countryCode === this.aviationEvent.countryCode).zoneName;
    } else {
      currentTimeZoneSimpleName = this.aviationEvent.timezone;
    } */
    // const currentTimeZoneSimpleName =  Object.keys(TimeZones).filter(it => TimeZones[it] === this.aviationEvent.timezone).pop();

    this.eventTypes = Object.values(AviationEventTypeDesc) as String[];
    const currentEventType = AviationEventTypeDesc[AviationEventType[this.aviationEvent.typeId]];
    this.eventStart = MomentTZ.tz(this.aviationEvent.startDateTime, 'UTC');
    this.eventStartDateString = this.eventStart.clone().tz(this.aviationEvent.timezone).format('dddd, MMMM Do, YYYY');
    this.eventStartTimeString = this.eventStart.clone().tz(this.aviationEvent.timezone).format('HH:mm');
    this.eventEnd = MomentTZ.tz(this.aviationEvent.endDateTime, 'UTC');
    this.eventEndDateString = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('dddd, MMMM Do, YYYY');
    this.eventEndTimeString = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('HH:mm');
    this.startDate = this.eventStart.clone().tz(this.aviationEvent.timezone).toDate();
    this.endDate = this.eventEnd.clone().tz(this.aviationEvent.timezone).toDate();
    this.startHour = this.eventStart.clone().tz(this.aviationEvent.timezone).format('h');
    this.startMin = this.eventStart.clone().tz(this.aviationEvent.timezone).format('mm');
    this.startAMPM = this.eventStart.clone().tz(this.aviationEvent.timezone).format('A');
    this.endHour = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('h');
    this.endMin = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('mm');
    this.endAMPM = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('A');


    this.formGroup = this._fb.group({
      format: [this.aviationEvent.format, [Validators.maxLength(32)]],
      venueLocationName: [this.aviationEvent.venueLocationName, Validators.required],
      address1: [this.aviationEvent.address1, Validators.required],
      address2: [this.aviationEvent.address2, Validators.nullValidator],
      address3: [this.aviationEvent.address3, Validators.nullValidator],
      city: [this.aviationEvent.city, Validators.required],
      isOnAirport: [!!this.aviationEvent.airportId, Validators.nullValidator],
      airport: [this.initialAirport, Validators.nullValidator],
      stateProvince: [this.aviationEvent.stateProvince, Validators.required],
      zipcode: [this.aviationEvent.zipcode, Validators.required],
      country: [this.initialCountryCode, Validators.required],
      internationalAirportId: [this.aviationEvent.internationalAirportId, Validators.nullValidator],
      internationalAirportName: [this.aviationEvent.internationalAirportName, Validators.nullValidator],
      latitude: [this.aviationEvent.latitude, [Validators.min(-90), Validators.max(90), Validators.pattern('^-?\\d+[.]\\d+$')]],
      longitude: [this.aviationEvent.longitude, [Validators.min(-180), Validators.max(180), Validators.pattern('^-?\\d+[.]\\d+$')]],
      overrideAddressLookup: [false, Validators.nullValidator],
      startDate: [this.startDate, Validators.required],
      endDate: [this.endDate, Validators.required],
      startHour: [this.startHour, Validators.required],
      startMin: [this.startMin, Validators.required],
      startAMPM: [this.startAMPM, Validators.required],
      endHour: [this.endHour, Validators.required],
      endMin: [this.endMin, Validators.required],
      endAMPM: [this.endAMPM, Validators.required],
      timezone: ['' || '', Validators.required],
      webinarRegistrationLink: [this.aviationEvent.webinarRegistrationLink,
        [Validators.maxLength(256), Validators.pattern(this.urlRegex)]],
      informationWebsite: [this.aviationEvent.informationWebsite,
        [Validators.maxLength(256), Validators.pattern(this.urlRegex)]],
      websiteCustomLabel: [this.aviationEvent.websiteCustomLabel, [Validators.maxLength(50)]],
      type: [currentEventType, [Validators.maxLength(256)]],
      cost: [this.aviationEvent.cost || 'Unknown', [Validators.maxLength(256)]]
    }, {updateOn: 'submit'});
    if (this.aviationEvent.format === 'Online') {
      this._setAddressValidation(false);
    } else {
      this._setAddressValidation(true);
    }
    await this.updateTimeZones(this.initialCountryCode);
    this.isLoading = false;
  }

  getStates(): String[] {
    const COUNTRY: String = this.formGroup.value.country;
    let resultArry: String[] = [];
    if (COUNTRY === 'US') {
      resultArry = eventUSStates;
    } else if (COUNTRY === 'CA') {
      resultArry = eventCanadianProvinces;
    }
    return resultArry;
  }

  showDateError(): boolean {
    const startDate: Date = this.formGroup.value.startDate;
    startDate.setHours(0, 0, 0, 0);
    const endDate: Date = this.formGroup.value.endDate;
    endDate.setHours(0, 0, 0, 0);
    let startHour: number = +this.formGroup.value.startHour;
    if (startHour === 12) {
      startHour = 0;
    }
    startHour = this.formGroup.value.startAMPM === 'PM' ? startHour + 12 : startHour;
    let endHour: number = +this.formGroup.value.endHour;
    if (endHour === 12) {
      endHour = 0;
    }
    endHour = this.formGroup.value.endAMPM === 'PM' ? endHour + 12 : endHour;

    const dateBoolean = startDate && endDate && startDate.getTime() > endDate.getTime();
    if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
      return startHour > endHour || (startHour === endHour && +this.formGroup.value.startMin > +this.formGroup.value.endMin);
    }
    return dateBoolean;
  }

  eventDateChange(type: string, event: MatDatepickerInputEvent<Date>) {
    const element = event.targetElement;
    if (element.getAttribute('name') === 'startDate') {
      this.endDateMin = event.value;
    }
  }

  private buildZonedTime(date: Date, time: string, timezone: String): string {
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const year = date.getFullYear();
    const localTime = MomentTZ.tz(`${year}-${month}-${day} ${time}`, timezone);
    return localTime.utc().format('MM/DD/YYYY h:mm:ss A');
  }

  async statesFieldChanged(event: MatSelectChange) {
    this.formGroup.patchValue({stateProvince: ''});
    this.formGroup.patchValue({city: ''});
    this.formGroup.patchValue({zipcode: ''});
    this.formGroup.patchValue({address1: ''});
    this.formGroup.patchValue({address2: ''});
    this.formGroup.patchValue({address3: ''});
    this.formGroup.patchValue({internationalAirportId: ''});
    this.formGroup.patchValue({internationalAirportName: ''});
    this.formGroup.patchValue({airport: ''});
    this.formGroup.patchValue({latitude: ''});
    this.formGroup.patchValue({longitude: ''});
    this.formGroup.patchValue({overrideAddressLookup: false});
    this.selectedAirport = null;
    if (['US', 'CA'].includes(this.formGroup.value.country)) {
      this.formGroup.controls['city'].setValidators(Validators.required);
      this.formGroup.controls['stateProvince'].setValidators(Validators.required);
      this.formGroup.controls['zipcode'].setValidators(Validators.required);
    } else {
      this.formGroup.controls['city'].setValidators([]);
      this.formGroup.controls['stateProvince'].setValidators([]);
      this.formGroup.controls['zipcode'].setValidators([]);
    }
    this._updateLatLngValidators();
    this.formGroup.controls['city'].updateValueAndValidity();
    this.formGroup.controls['stateProvince'].updateValueAndValidity();
    this.formGroup.controls['zipcode'].updateValueAndValidity();
    this.currentCountry = event.source.triggerValue;
    await this.updateTimeZones(event.value, true);
  }

  overrideAddressLookupChanged() {
    this._updateLatLngValidators();
  }

  private _updateLatLngValidators() {
    if (this.showLatLng()) {
      this.formGroup.controls['latitude'].setValidators([Validators.required, Validators.min(-90), Validators.max(90),
        Validators.pattern('^-?\\d+[.]\\d+$')]);
      this.formGroup.controls['longitude'].setValidators([Validators.required, Validators.min(-180), Validators.max(180),
        Validators.pattern('^-?\\d+[.]\\d+$')]);
    } else {
      this.formGroup.controls['latitude'].setValidators([]);
      this.formGroup.controls['longitude'].setValidators([]);
    }
    this.formGroup.controls['latitude'].updateValueAndValidity();
    this.formGroup.controls['longitude'].updateValueAndValidity();
  }


  airportChange(airport: AirportSearchResult) {
    this.selectedAirport = airport;
    this.initialAirport = '';
    this.airportSelectionError = false;
  }

  airportSearchFocusOut(event: Event) {
    if (this.selectedAirport) {
      this.formGroup.patchValue({airport: `${this.selectedAirport.identifier}: ${this.selectedAirport.name}`});
    } else {
      this.formGroup.patchValue({airport: ''});
    }
  }

  airportQueryKeyUp(event:  KeyboardEvent) {
    const val = (event.target as HTMLInputElement).value;
    if (val.length < 3) {
      if (!val || val.length === 0) {
        this.airportSelectionError = false;
        this.selectedAirport = null;
        this.airports = [];
      }
      return;
    }
    this._airportService.getAirportsBySearchTerm(val, true).then((res) => {
      this.airports = res;
    }).catch(err => console.error(err));
  }

  eventFormatSelectionChange (event: MatSelectChange) {
    const format = event.value;
    if (format === 'Online') {
      this._setAddressValidation(false);
      this.showAddressFields = false;
      this.formGroup.patchValue({address1: ''});
      this.formGroup.patchValue({address2: ''});
      this.formGroup.patchValue({address3: ''});
      this.formGroup.patchValue({city: ''});
      this.formGroup.patchValue({state: ''});
      this.formGroup.patchValue({zipcode: ''});
      this.formGroup.patchValue({airport: ''});
      this.formGroup.patchValue({internationalAirportId: ''});
      this.formGroup.patchValue({internationalAirportName: ''});
      this.formGroup.patchValue({latitude: ''});
      this.formGroup.patchValue({longitude: ''});
      this.formGroup.patchValue({overrideAddressLookup: false});
      this.formGroup.controls['latitude'].setValidators([]);
      this.formGroup.controls['longitude'].setValidators([]);
      this.formGroup.controls['latitude'].updateValueAndValidity();
      this.formGroup.controls['longitude'].updateValueAndValidity();
    } else {
      this._setAddressValidation();
      // this.formGroup.patchValue({country: 'United States'});
      this.showAddressFields = true;
    }
  }

  private _setAddressValidation(validationIsRequired: Boolean = true) {
    this.formGroup.removeControl('address1');
    this.formGroup.removeControl('stateProvince');
    this.formGroup.removeControl('city');
    this.formGroup.removeControl('zipcode');
    // this.formGroup.removeControl('country');
    this.formGroup.removeControl('venueLocationName');
    if (validationIsRequired) {
      this.formGroup.addControl('address1', new FormControl(this.aviationEvent.address1, Validators.required));
      if (['US', 'CA'].includes(this.formGroup.value.country)) {
        this.formGroup.addControl('stateProvince', new FormControl(this.aviationEvent.stateProvince, Validators.required));
        this.formGroup.addControl('city', new FormControl(this.aviationEvent.city, Validators.required));
        this.formGroup.addControl('zipcode', new FormControl(this.aviationEvent.zipcode, Validators.required));
      } else {
        this.formGroup.addControl('stateProvince', new FormControl('', Validators.nullValidator));
        this.formGroup.addControl('city', new FormControl('', Validators.nullValidator));
        this.formGroup.addControl('zipcode', new FormControl('', Validators.nullValidator));
      }
      if (this.showLatLng()) {
        this.formGroup.addControl('latitude', new FormControl('', [Validators.required,
          Validators.min(-90), Validators.max(90), Validators.pattern('^-?\\d+[.]\\d+$')]));
        this.formGroup.addControl('longitude', new FormControl('', [Validators.required,
          Validators.min(-180), Validators.max(180), Validators.pattern('^-?\\d+[.]\\d+$')]));
      } else {
        this.formGroup.addControl('latitude', new FormControl('', Validators.nullValidator));
        this.formGroup.addControl('longitude', new FormControl('', Validators.nullValidator));
      }
      // this.formGroup.addControl('country', new FormControl(this.aviationEvent.country, Validators.required));
      this.formGroup.addControl('venueLocationName', new FormControl(this.aviationEvent.venueLocationName, Validators.required));
    } else {
      this.formGroup.addControl('address1', new FormControl('', Validators.nullValidator));
      this.formGroup.addControl('stateProvince', new FormControl('', Validators.nullValidator));
      this.formGroup.addControl('city', new FormControl('', Validators.nullValidator));
      this.formGroup.addControl('zipcode', new FormControl('', Validators.nullValidator));
      // this.formGroup.addControl('country', new FormControl('', Validators.nullValidator));
      this.formGroup.addControl('venueLocationName', new FormControl('', Validators.nullValidator));
    }
  }

  close(result = {cancelBtnClicked: true, success: false}) {
    if (result) {
      this._matDialogRef.close(result);
    }
  }

  cancel() {
    this._matDialogRef.close(null);
  }

  public isUSorCA(): boolean {
    if (this.formGroup.value.country && ['US', 'CA'].includes(this.formGroup.value.country)) {
      return true;
    }
    return false;
  }

  public showLatLng(): boolean {
    return !this.isUSorCA() || (this.isFlightDeckAdmin && !!this.formGroup.value.overrideAddressLookup);
  }

  private getRegionCodeFromCountry(country: string) {
    switch (country.toLowerCase()) {
      case 'us':
        return 'US';
      case 'ca':
        return 'CA';
      default:
        return null;
    }
  }

  private async _getAviationEventFromFormValue(formValue: any): Promise<AviationEvent> {
    let postalAddress: GoogleMapsValidationModel.PostalAddress = null;
    const regionCode = this.getRegionCodeFromCountry(formValue.country);
    this.aviationEvent.address1 = formValue.address1;
    this.aviationEvent.address2 = formValue.address2 && formValue.address2.trim();
    this.aviationEvent.address3 = formValue.address3 && formValue.address3.trim();
    this.aviationEvent.city = formValue.city;
    this.aviationEvent.stateProvince = formValue.stateProvince;
    this.aviationEvent.zipcode = formValue.zipcode;
    this.aviationEvent.country = this.currentCountry;
    this.aviationEvent.countryCode = formValue.country;
    this.aviationEvent.internationalAirportId = formValue.internationalAirportId;
    this.aviationEvent.internationalAirportName = formValue.internationalAirportName;

    if (regionCode && formValue.address1 && formValue.city && formValue.stateProvince && formValue.zipcode && formValue.country
      && !formValue.overrideAddressLookup) {
      postalAddress = {
        regionCode: regionCode,
        languageCode: 'EN',
        postalCode: this.aviationEvent.zipcode,
        locality: this.aviationEvent.city,
        addressLines: [this.aviationEvent.address1],
      };

      const validationResponse  = await this._addressValidationService
        .checkAddress(new GoogleMapsValidationModel.RequestBody(postalAddress));
      const result = validationResponse.result;
      const verdict = result.verdict;
      const granularityIsCorrect = verdict.geocodeGranularity === Granularity.PREMISE ||
        verdict.geocodeGranularity === Granularity.SUB_PREMISE;

      if (!verdict) {
        this._snackBar.open('Address could not be validated, please try again', null, {duration: 5000});
        throw new Error('Address validation failed');
      }

      if (granularityIsCorrect && !verdict.hasUnconfirmedComponents) {
        const address = result.address.postalAddress;
        this.aviationEvent.city = address.locality;
        this.aviationEvent.zipcode = address.postalCode.split('-')[0];
        this.aviationEvent.address1 = address.addressLines[0];
        this.aviationEvent.stateProvince = address.administrativeArea;
        const location =
          result.geocode && result.geocode.location ?
            result.geocode.location : null;
        this.aviationEvent.location = new LatLon(location.latitude, location.longitude);
        this.aviationEvent.latitude = location.latitude;
        this.aviationEvent.longitude = location.longitude;
      } else {
        const errorMsg = 'Invalid Address Enter a Correct Address';
        if (verdict.hasUnconfirmedComponents && result.address.missingComponentTypes) {
          this._snackBar
            .open( `${errorMsg}, check: ${result.address.missingComponentTypes.join(', ')}`, null, {duration: 10000});
        } else {
          this._snackBar.open(errorMsg, null, {duration: 10000});
        }
        throw new Error('Invalid Address');
      }
    } else {
      // International address
      this.aviationEvent.address1 = formValue.address1;
      this.aviationEvent.address2 = formValue.address2;
      this.aviationEvent.address3 = formValue.address3;
      this.aviationEvent.location = new LatLon(formValue.latitude, formValue.longitude);
      this.aviationEvent.latitude = formValue.latitude;
      this.aviationEvent.longitude = formValue.longitude;
    }
    return this.aviationEvent;
  }

  async save () {
    const TEMP_aviationEvent = this.aviationEvent;
    let tzName = this.formGroup.value.timezone;
    if (this.formGroup.value.country === 'US') {
      tzName = USTimezones.find(i => i.zoneName === this.formGroup.value.timezone).countryCode;
    }
    const startDate = new Date(this.formGroup.value.startDate);
    let startTime = +this.formGroup.value.startHour < 10 ? '0' + this.formGroup.value.startHour : this.formGroup.value.startHour;
    // if startTime is 12:00 AM, set to 00:00
    if (+this.formGroup.value.startHour === 12 && this.formGroup.value.startAMPM === 'AM') {
      startTime = '00';
    } else {
      startTime = this.formGroup.value.startAMPM === 'PM' && +this.formGroup.value.startHour < 12 ? (+startTime + 12).toString() :
        startTime;
    }
    startTime = startTime + ':' + this.formGroup.value.startMin;
    const endDate = new Date(this.formGroup.value.endDate);
    let endTime = +this.formGroup.value.endHour < 10 ? '0' + this.formGroup.value.endHour : this.formGroup.value.endHour;
    // if endTime is 12:00 AM, set to 00:00
    if (+this.formGroup.value.endHour === 12 && this.formGroup.value.endAMPM === 'AM') {
      endTime = '00';
    } else {
      endTime = this.formGroup.value.endAMPM === 'PM' && +this.formGroup.value.endHour < 12 ? (+endTime + 12).toString() : endTime;
    }
    endTime = endTime + ':' + this.formGroup.value.endMin;

    this.aviationEvent.format = this.formGroup.value.format;
    if (this.formGroup.value.airport.trim() && this.selectedAirport == null && this.initialAirportId === 0) {
      this.airportSelectionError = true;
      this._snackBar.open('Invalid Airport Selected', null, {duration: 5000});
      return;
    } else if (this.formGroup.invalid) {
      this._snackBar.open('Form field validation failed. check form values', null, {duration: 5000});
      return;
    } else if (this.showDateError()) {
      this._snackBar.open('Start time cannot be after end time, check end time is not before start time', null, {duration: 5000});
      return;
    }

    if (this.formGroup.value.venueLocationName && this.formGroup.value.venueLocationName.trim().length) {
      this.aviationEvent.venueLocationName = this.formGroup.value.venueLocationName.trim();
    } else {
      this.aviationEvent.venueLocationName = null;
    }
    this.aviationEvent.address1 = this.formGroup.value.address1;
    this.aviationEvent.address2 = this.formGroup.value.address2;
    this.aviationEvent.city = this.formGroup.value.city;
    this.aviationEvent.stateProvince = this.formGroup.value.stateProvince;
    this.aviationEvent.zipcode = this.formGroup.value.zipcode;
    this.aviationEvent.country = this.formGroup.value.country;
    if (this.formGroup.value.airport.trim().length === 0) {
      this.aviationEvent.airportId = null;
    } else if (!this.selectedAirport && this.initialAirportId !== 0) {
      this.aviationEvent.airportId = this.initialAirportId;
    } else {
      this.aviationEvent.airportId = this.selectedAirport ? this.selectedAirport.airportId : null;
    }

    if (this.formGroup.value.webinarRegistrationLink && this.formGroup.value.webinarRegistrationLink.trim().length) {
      this.aviationEvent.webinarRegistrationLink = this.formGroup.value.webinarRegistrationLink.trim();
    } else {
      this.aviationEvent.webinarRegistrationLink = null;
    }
    if (this.formGroup.value.informationWebsite && this.formGroup.value.informationWebsite.trim().length) {
      this.aviationEvent.informationWebsite = this.formGroup.value.informationWebsite.trim();
    } else {
      this.aviationEvent.informationWebsite = null;
    }
    if (this.formGroup.value.websiteCustomLabel && this.formGroup.value.websiteCustomLabel.trim().length) {
      this.aviationEvent.websiteCustomLabel = this.formGroup.value.websiteCustomLabel.trim();
    } else {
      this.aviationEvent.websiteCustomLabel = null;
    }
    if (this.formGroup.value.cost && this.formGroup.value.cost.trim().length && this.formGroup.value.cost.trim() !== 'Unknown') {
      this.aviationEvent.cost = this.formGroup.value.cost.trim();
    } else {
      this.aviationEvent.cost = null;
    }
    // this.aviationEvent.format = this.formGroup.value.format;
    this.aviationEvent.type = AviationEvent.parseEventFromString(this.formGroup.value.type);
    this.aviationEvent.typeId = this.aviationEvent.type;
    this.aviationEvent.timezone = tzName;
    this.aviationEvent.startDateTimeUTC = this.buildZonedTime(startDate, startTime, tzName);
    this.aviationEvent.endDateTimeUTC = this.buildZonedTime(endDate, endTime, tzName);

    // if (this.formGroup.value.address1 && this.formGroup.value.city && this.formGroup.value.stateProvince &&
    // this.formGroup.value.zipcode) {
    if (true) {
      this._getAviationEventFromFormValue(this.formGroup.value)
        .then(async event => {
          try {
            await this._aviationEventsService.updateAviationEvent(this.aviationEvent);
            this.close({cancelBtnClicked: false, success: true});
          } catch (e) {
            this.aviationEvent = TEMP_aviationEvent;
            this.close({cancelBtnClicked: false, success: false});
          }
        })
        .catch(err => console.error(err));

    } else {
      try {
        await this._aviationEventsService.updateAviationEvent(this.aviationEvent);
        this.close({cancelBtnClicked: false, success: true});
      } catch (e) {
        this.aviationEvent = TEMP_aviationEvent;
        this.close({cancelBtnClicked: false, success: false});
      }
    }
  }

  async updateTimeZones(countryCode: string, countryChange: boolean = false) {
    if (countryCode === 'US') {
      this.timezones = USTimezones;
      if (countryChange) {
        this.formGroup.patchValue({timezone: ''});
      } else {
        const displayTimezone = USTimezones.find(i => i.countryCode === this.aviationEvent.timezone).zoneName;
        this.formGroup.patchValue({timezone: displayTimezone});
      }
    } else {
      this.timezones = await this._aviationEventsService.getTimezones(countryCode);
      if (countryChange) {
        if (this.timezones.length === 1) {
          this.formGroup.patchValue({timezone: this.timezones[0].zoneName});
        } else {
          this.formGroup.patchValue({timezone: ''});
        }
      } else {
        this.formGroup.patchValue({timezone: this.aviationEvent.timezone});
      }
    }
  }
}
